import {
  usePlayerActions,
  usePlayerStateReader,
} from "@utils/player/core/usePlayer";
import { SharedValue, useSharedValue } from "react-native-reanimated";
import { createContext, useContext } from "react";
import { useWindowDimensions } from "react-native";
import { getViewPort } from "@utils/web/config";
import { useForyouQueue, usePlayer, usePlaylistQueue } from "@utils/player";
import { selectIsAutoPlayEnabled } from "@utils/player/core/selectors";

import type { QueueItem } from "@utils/player/utils/queue.types";

const NUMBER_OF_SLOTS = 4;

export type SharedSwipeData = {
  activeIndex: number | undefined;
  baseHeight: number;
  items: QueueItem[] | undefined;
  initialOffset: number;
  numberOfSlots: number;
  playPauseSongAtIndex: (index: number) => void;
  progress: SharedValue<number>;
  targetOffset: SharedValue<number>;
};

export const ForyouSharedSwipeContext = createContext<SharedSwipeData | null>(
  null,
);
export const PlaylistSharedSwipeContext = createContext<SharedSwipeData | null>(
  null,
);

export function useForyouSharedSwipe() {
  const context = useContext(ForyouSharedSwipeContext);
  if (!context) {
    throw new Error(
      "useForyouSharedSwipe must be used within a SharedSwipeProvider",
    );
  }
  return context;
}

export function usePlaylistSharedSwipe() {
  const context = useContext(PlaylistSharedSwipeContext);
  if (!context) {
    throw new Error(
      "usePlaylistSharedSwipe must be used within a SharedSwipeProvider",
    );
  }
  return context;
}

const useSharedSwipeBase = (
  activeIndex: number | undefined,
  items: QueueItem[] | undefined,
) => {
  const playerActions = usePlayerActions();

  const window = useWindowDimensions();
  const baseHeight = getViewPort(window).height;
  const isAutoPlayEnabled = usePlayer(selectIsAutoPlayEnabled);

  const playFromQueueItem = playerActions.playFromQueueItem;

  const progress = useSharedValue(0);
  const initialOffset = (activeIndex ?? 0) * baseHeight;
  const targetOffset = useSharedValue(initialOffset);

  const playPauseSongAtIndex = (index: number) => {
    if (items && items[index]) {
      playFromQueueItem(items[index].qid, { autoPlay: isAutoPlayEnabled });
    } else {
      console.error(
        `::playPauseSongAtIndex:: Trying to play index: ${index}, but no song queue found`,
      );
    }
  };

  return {
    activeIndex,
    baseHeight,
    items,
    initialOffset,
    numberOfSlots: NUMBER_OF_SLOTS,
    playPauseSongAtIndex,
    progress,
    targetOffset,
  };
};

export function useCreateForyouSharedSwipe(): SharedSwipeData {
  const foryouQueue = useForyouQueue();
  const props = useSharedSwipeBase(
    foryouQueue?.currentIndex,
    foryouQueue?.items,
  );

  return props;
}

export function useCreatePlaylistSharedSwipe(): SharedSwipeData {
  const playlistQueue = usePlaylistQueue();
  const props = useSharedSwipeBase(
    playlistQueue?.currentIndex,
    playlistQueue?.items,
  );
  return props;
}

// -------------------------------------------------------------------------------------
// New (perf-focused) shared swipe engine:
// Only shares Reanimated shared values between SwipeLists (sync),
// while each SwipeList subscribes to its own queue (prevents unrelated rerenders).
// -------------------------------------------------------------------------------------

export type SharedSwipeEngine = {
  baseHeight: number;
  numberOfSlots: number;
  progress: SharedValue<number>;
  targetOffset: SharedValue<number>;
};

export const ForyouSwipeEngineContext = createContext<SharedSwipeEngine | null>(
  null,
);
export const PlaylistSwipeEngineContext =
  createContext<SharedSwipeEngine | null>(null);

export function useForyouSwipeEngine(): SharedSwipeEngine {
  const ctx = useContext(ForyouSwipeEngineContext);
  if (!ctx) {
    throw new Error(
      "useForyouSwipeEngine must be used within a SharedSwipeProvider",
    );
  }
  return ctx;
}

export function usePlaylistSwipeEngine(): SharedSwipeEngine {
  const ctx = useContext(PlaylistSwipeEngineContext);
  if (!ctx) {
    throw new Error(
      "usePlaylistSwipeEngine must be used within a SharedSwipeProvider",
    );
  }
  return ctx;
}

export function useCreateForyouSwipeEngine(): SharedSwipeEngine {
  const getPlayerState = usePlayerStateReader();

  const window = useWindowDimensions();
  const baseHeight = getViewPort(window).height;

  const initialIndex = getPlayerState().foryouQueue?.currentIndex ?? 0;
  const initialOffset = initialIndex * baseHeight;

  const progress = useSharedValue(initialOffset);
  const targetOffset = useSharedValue(initialOffset);

  return {
    baseHeight,
    numberOfSlots: NUMBER_OF_SLOTS,
    progress,
    targetOffset,
  };
}

export function useCreatePlaylistSwipeEngine(): SharedSwipeEngine {
  const getPlayerState = usePlayerStateReader();

  const window = useWindowDimensions();
  const baseHeight = getViewPort(window).height;

  const initialIndex = getPlayerState().playlistQueue?.currentIndex ?? 0;
  const initialOffset = initialIndex * baseHeight;

  const progress = useSharedValue(initialOffset);
  const targetOffset = useSharedValue(initialOffset);

  return {
    baseHeight,
    numberOfSlots: NUMBER_OF_SLOTS,
    progress,
    targetOffset,
  };
}
